import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

// Types
export interface CohortReport {
  cohort_id: string;
  cohort_name: string;
  generated_at: string;
  total_students: number;
  total_assessments: number;
  average_scores: {
    physical: number;
    linguistic: number;
    cognitive: number;
    social_emotional: number;
    overall: number;
  };
  report_url?: string;
}

// Report hooks
export function useCohortReport(cohortId: string) {
  return useQuery<CohortReport>({
    queryKey: ['cohortReport', cohortId],
    queryFn: async () => {
      const response = await apiClient.get<CohortReport>(`/reports/cohorts/${cohortId}/`);
      return response.data;
    },
    enabled: !!cohortId,
  });
}

export function useExportCohortReport() {
  return useMutation({
    mutationFn: async (cohortId: string) => {
      const response = await apiClient.get(`/reports/cohorts/export/`, {
        params: { cohort_id: cohortId },
        responseType: 'blob',
      });
      return response.data;
    },
  });
}

export function useDownloadReportPdf() {
  return useMutation({
    mutationFn: async ({
      reportId,
      simplified = false,
      filename = 'oracy-report.pdf',
    }: {
      reportId: string;
      simplified?: boolean;
      filename?: string;
    }) => {
      const response = await apiClient.get(`/reports/reports/${reportId}/download_pdf/`, {
        params: simplified ? { simplified: 'true' } : {},
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  });
}
